import type { FlagSeverity, MatchFlag } from '../../api/types';

interface Props {
  /** null/undefined = never computed (legacy rows); [] = computed, none fired. */
  flags?: MatchFlag[] | null;
  /** When set, show at most `max` chips plus a `+N` overflow chip. */
  max?: number;
}

// Colors deliberately mirror consentBadge (MatchesPage): -100 bg / -800 text.
const SEVERITY_CLASS: Record<FlagSeverity, string> = {
  info: 'bg-gray-100 text-gray-700',
  warn: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800',
};

/** Compact chips for deterministic red flags; detail text via tooltip. */
export default function FlagChips({ flags, max }: Props) {
  if (!flags?.length) return null;

  // Dedupe by flag+detail — the same id can legitimately fire twice with
  // different detail (e.g. ambiguity_midpoint for location AND time).
  const seen = new Set<string>();
  const unique = flags.filter((f) => {
    const key = `${f.flag}:${f.detail}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const visible = max != null ? unique.slice(0, max) : unique;
  const overflow = unique.length - visible.length;

  return (
    <>
      {visible.map((f) => (
        <span
          key={`${f.flag}:${f.detail}`}
          title={f.detail}
          className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${SEVERITY_CLASS[f.severity]}`}
        >
          {f.flag}
        </span>
      ))}
      {overflow > 0 && (
        <span
          title={unique
            .slice(visible.length)
            .map((f) => `[${f.severity}] ${f.flag}`)
            .join(', ')}
          className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap"
        >
          +{overflow}
        </span>
      )}
    </>
  );
}
